import type { PurchaseOrderDetail } from "@/modules/inventory/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { PO_STATUS_LABELS, poItemTypeLabel } from "./purchaseLaoLabels";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function buildPurchaseOrderPrintHtml(po: PurchaseOrderDetail): string {
  const statusLabel =
    PO_STATUS_LABELS[po.status as keyof typeof PO_STATUS_LABELS] ?? po.status;

  const lineRows = po.lines
    .map(
      (line) => `
        <tr>
          <td style="padding:6px 8px;border-bottom:1px solid #eee">${line.lineNo}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #eee">
            <strong>${escapeHtml(line.itemName)}</strong>
            <div style="font-size:11px;color:#666">${escapeHtml(poItemTypeLabel(line.itemType))} · ${escapeHtml(line.unit)}</div>
          </td>
          <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${formatQtyDisplay(line.qtyOrdered)}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${formatLakDisplay(line.unitCostLak)}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${formatLakDisplay(line.lineTotalLak)}</td>
        </tr>`,
    )
    .join("");

  const noteBlock = po.note?.trim()
    ? `<p style="margin:8px 0"><strong>ໝາຍເຫດ:</strong> ${escapeHtml(po.note)}</p>`
    : "";

  return `<!DOCTYPE html>
<html lang="lo">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(po.poNo)}</title>
  <style>
    @page { margin: 16mm; }
    body { font-family: system-ui, "Noto Sans Lao", sans-serif; font-size: 13px; line-height: 1.45; color: #111; max-width: 720px; margin: 0 auto; padding: 12px; }
    h1 { font-size: 20px; margin: 0 0 4px 0; }
    .meta { color: #444; font-size: 12px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; padding: 8px; border-bottom: 2px solid #333; font-size: 11px; }
    .total { margin-top: 12px; text-align: right; font-size: 15px; font-weight: bold; }
    .foot { margin-top: 24px; font-size: 11px; color: #666; border-top: 1px solid #ddd; padding-top: 8px; }
  </style>
</head>
<body>
  <h1>ໃບສັ່ງຊື້ (Purchase Order)</h1>
  <div class="meta">
    <p style="margin:2px 0"><strong>${escapeHtml(po.poNo)}</strong> · ${escapeHtml(statusLabel)}</p>
    <p style="margin:2px 0"><strong>ຜູ້ສະໜອງ:</strong> ${escapeHtml(po.supplierName)}${po.supplierCode ? ` (${escapeHtml(po.supplierCode)})` : ""}</p>
    <p style="margin:2px 0"><strong>ສາຂາຮັບ:</strong> ${escapeHtml(po.branchName)} (${escapeHtml(po.branchCode)})</p>
    <p style="margin:2px 0"><strong>ວັນທີສັ່ງ:</strong> ${formatDateLocal(po.orderDate)}${po.expectedDate ? ` · <strong>ຄາດຮັບ:</strong> ${formatDateLocal(po.expectedDate)}` : ""}</p>
  </div>
  ${noteBlock}
  <table>
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th>ລາຍການ</th>
        <th style="text-align:right;width:80px">ຈຳນວນ</th>
        <th style="text-align:right;width:90px">₭/ຫນ່ວຍ</th>
        <th style="text-align:right;width:100px">ລວມ</th>
      </tr>
    </thead>
    <tbody>${lineRows}</tbody>
  </table>
  <p class="total">ລວມທັງໝົດ: ${formatLakDisplay(po.subtotalLak)} ${po.currency}</p>
  <div class="foot">ພິມຈາກ Mineral Admin · ໃຊ້ «ບັນທຶກເປັນ PDF» ໃນ dialog ພິມ</div>
  <script>
    window.onload = function() { window.print(); };
    window.onafterprint = function() { window.close(); };
  </script>
</body>
</html>`;
}

export function openPurchaseOrderPrint(po: PurchaseOrderDetail): void {
  const html = buildPurchaseOrderPrintHtml(po);
  const w = window.open("", "_blank");
  if (!w) {
    throw new Error("POPUP_BLOCKED");
  }
  w.document.write(html);
  w.document.close();
}
